import { useState, useEffect, useRef, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import { FaEye, FaTrash } from "react-icons/fa";
import { toast } from 'react-toastify'
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'

import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const STATUSES = ['', 'Open', 'Assigned', 'In Progress', 'Resolved']
const PRIORITIES = ['', 'Low', 'Medium', 'High', 'Urgent']

const priorityBadge = {
    Low: 'secondary',
    Medium: 'warning',
    High: 'danger',
    Urgent: 'dark',
}

const statusBadge = {
    Open: 'primary',
    Assigned: 'info',
    'In Progress': 'warning',
    Resolved: 'success',
    Closed: 'secondary',
}

const Complaints = () => {
    const [complaints, setComplaints] = useState([])
    const [categories, setCategories] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [show, setShow] = useState(false)
    const [publicID, setPublicID] = useState()
    const gridRef = useRef()
    const navigate = useNavigate()

    const today = new Date().toISOString().split('T')[0]
    const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]

    const [filters, setFilters] = useState({
        _status: '',
        priority: '',
        category_id: '',
        fdate: firstOfMonth,
        tdate: today,
        limit: 200,
    })

    useEffect(() => {
        let isMounted = true
        const getCategories = async () => {
            try {
                const response = await Server.get('/complaint-categories/')
                if (isMounted) setCategories(response?.data || [])
            } catch (_) {}
        }
        getCategories()
        return () => (isMounted = false)
    }, [])

    const fetchComplaints = async () => {
        setIsLoading(true)
        try {
            const params = new URLSearchParams()
            if (filters._status) params.append('_status', filters._status)
            if (filters.priority) params.append('priority', filters.priority)
            if (filters.category_id) params.append('category_id', filters.category_id)
            if (filters.fdate) params.append('fdate', filters.fdate)
            if (filters.tdate) params.append('tdate', filters.tdate)
            if (filters.limit) params.append('limit', filters.limit)

            const response = await Server.get(`/complaints/?${params.toString()}`)
            setComplaints(response?.data || [])
        } catch (error) {
            if (error?.response?.status === 401) {
                localStorage.removeItem('user')
                navigate('/login')
            } else if (error?.response?.status === 403) {
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            } else {
                toast.error(error?.response?.data?.detail || 'Failed to load complaints')
            }
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchComplaints()
    }, [])

    const onFilterChange = (e) => {
        setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }))
    }

    const onSearch = (e) => {
        e.preventDefault()
        fetchComplaints()
    }

    const handleClose = () => {
        if (isDeleting) return
        setShow(false)
    }
    const handleShow = (public_id) => {
        setShow(true)
        setPublicID(public_id)
    }

    const handleDelete = async (public_id) => {
        setIsDeleting(true)
        try {
            const response = await Server.delete(`/complaints/${public_id}`)
            if (response.status === 200) {
                toast.success(response.data.detail)
                setComplaints(prev => prev.filter(c => c.public_id !== public_id))
            } else {
                toast.error(response?.data?.detail || 'Failed to delete complaint')
            }
        } catch (error) {
            if (error?.response?.status === 401) {
                navigate('/login')
            } else if (error?.response?.status === 403) {
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            } else {
                toast.error(error?.response?.data?.detail || 'Failed to delete complaint')
            }
        } finally {
            setIsDeleting(false)
            setShow(false)
        }
    }

    const defaultColDef = useMemo(() => ({ filterParams: { buttons: [] } }), []);

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 60,
            valueFormatter: (params) => {
                if (isNaN(params.node.id)) return "";
                return `${Number(params.node.rowIndex) + 1}`;
            },
        },
        {
            headerName: "Actions",
            pinned: 'left',
            minWidth: 110,
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                return (
                    <div className="d-flex gap-1">
                        <Link className="btn btn-outline-info" to="/complaint-detail" state={{ complaint: e.data }} title="View complaint"><FaEye/></Link>
                        <button className="btn btn-outline-danger" onClick={() => handleShow(e.data.public_id)} title="Delete complaint"><FaTrash/></button>
                    </div>
                )
            },
        },
        { headerName: "Customer", field: "customer_name", sortable: true, filter: true },
        { headerName: "Contact", field: "contact_number", sortable: true, filter: true },
        { headerName: "Alt. Number", field: "alternative_number", sortable: true, filter: true },
        { headerName: "Village", field: "village_name", sortable: true, filter: true },
        { headerName: "Zone", field: "zone_name", sortable: true, filter: true },
        {
            headerName: "Priority",
            field: "priority",
            sortable: true,
            filter: true,
            cellRenderer: (e) => {
                if (!e.data) return null;
                const color = priorityBadge[e.data.priority] || 'secondary';
                return <span className={`badge bg-${color}`}>{e.data.priority}</span>
            }
        },
        {
            headerName: "Status",
            field: "status",
            sortable: true,
            filter: true,
            cellRenderer: (e) => {
                if (!e.data) return null;
                const color = statusBadge[e.data.status] || 'secondary';
                return <span className={`badge bg-${color}`}>{e.data.status}</span>
            }
        },
        { headerName: "Category", field: "category", sortable: true, filter: true },
        { headerName: "Response Type", field: "response_type", sortable: true, filter: true },
        { headerName: "Assigned To", field: "assigned_to", sortable: true, filter: true },
        { headerName: "Due Date", field: "due_date", sortable: true, filter: true },
        { headerName: "Created At", field: "created_at", sortable: true, filter: true },
        { headerName: "Created By", field: "created_by", sortable: true, filter: true },
    ], []);

    const onFirstDataRendered = (params) => { params.api.autoSizeAllColumns(); }
    const onGridSizeChanged = (params) => { params.api.autoSizeAllColumns(); }

    return (
        <>
            <div className="row mb-2">
                <div className="col-md-12">
                    <Link to='/add-complaint' className="btn btn-outline-info" style={{float: 'right'}}>Add Complaint</Link>
                </div>
            </div>

            <div className="card mb-3">
                <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                    <h3 className="card-title">Complaints</h3>
                </div>
                <div className="card-body">
                    <form onSubmit={onSearch}>
                        <div className="row g-2 align-items-end">
                            <div className="col-md-2">
                                <label className="form-label">Status</label>
                                <select className="form-select" name="_status" value={filters._status} onChange={onFilterChange}>
                                    {STATUSES.map(s => <option key={s} value={s}>{s || 'All'}</option>)}
                                </select>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label">Priority</label>
                                <select className="form-select" name="priority" value={filters.priority} onChange={onFilterChange}>
                                    {PRIORITIES.map(p => <option key={p} value={p}>{p || 'All'}</option>)}
                                </select>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label">Category</label>
                                <select className="form-select" name="category_id" value={filters.category_id} onChange={onFilterChange}>
                                    <option value="">All</option>
                                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label">From</label>
                                <input type="date" className="form-control" name="fdate" value={filters.fdate} onChange={onFilterChange} max={new Date().toISOString().split('T')[0]}/>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label">To</label>
                                <input type="date" className="form-control" name="tdate" value={filters.tdate} onChange={onFilterChange}/>
                            </div>
                            <div className="col-md-1">
                                <label className="form-label">Limit</label>
                                <input type="number" className="form-control" name="limit" value={filters.limit} onChange={onFilterChange} min={1}/>
                            </div>
                            <div className="col-md-1">
                                <button type="submit" className="btn btn-outline-info w-100" disabled={isLoading}>
                                    {isLoading ? '…' : 'Search'}
                                </button>
                            </div>
                        </div>
                    </form>

                    <div className="ag-theme-alpine responsive-aggrid mt-3" style={{ height: 500, width: '100%' }}>
                        <AgGridReact
                            suppressMenuHide={true}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            defaultColDef={defaultColDef}
                            rowData={complaints}
                            domLayout={"normal"}
                            animateRows={true}
                            pagination={true}
                            paginationPageSize={50}
                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}
                            ref={gridRef}
                            columnDefs={columnDefs}
                        />
                    </div>
                </div>
            </div>

            <Modal show={show} onHide={handleClose} backdrop={isDeleting ? 'static' : true}>
                <Modal.Header closeButton={!isDeleting}>
                    <Modal.Title>Delete Alert</Modal.Title>
                </Modal.Header>
                <Modal.Body>Are you sure you want to delete this complaint?</Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleClose} disabled={isDeleting}>No</Button>
                    <Button variant="danger" onClick={() => handleDelete(publicID)} disabled={isDeleting}>
                        {isDeleting ? 'Deleting…' : 'Yes'}
                    </Button>
                </Modal.Footer>
            </Modal>
            <br/>
        </>
    )
}

export default Complaints
